// React and Hooks
import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'

// Icons
import { MdOutlineDelete } from 'react-icons/md'
import { CiEdit } from 'react-icons/ci'

// Components
import Button from '../Custom/Buttons/Button'
import CustomToast from '../Custom/CustomToast'

// Utils
import axiosInterceptorInstance from '../../utils/axios/axiosInterceptorInstance'
import { useDebounce } from '../../hooks/useDebounce'
import configManager from '../../config/configManager'

// Assets
import brokenImg from '../../assets/images/brokenImg.png'

const Joints = ({ userPermissions }) => {
  const configData = configManager()
  const [loading, setLoading] = useState(false)

  const [page, setPage] = useState(1)
  const [limit] = useState(50)
  const [search, setSearch] = useState('')

  const [joints, setJoints] = useState([])
  const [moreAvailable, setMoreAvailable] = useState(true)

  const debouncedSearchTerm = useDebounce(search, 300)

  const [deleteJoints, setDeleteJoint] = useState([])
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    setPage(1)
    setJoints([])
    fetchJoints({
      page,
      limit,
      category: 'joint',
      search: debouncedSearchTerm,
    })
  }, [debouncedSearchTerm])

  const fetchJoints = async (params) => {
    const { page = 1, limit = 50, category = 'joint', search = null } = params

    const urlParams = new URLSearchParams({
      page,
      limit,
      category: 'joint',
      ...(search && { search }),
    })

    setLoading(true)
    try {
      const { data } = await axiosInterceptorInstance.post(
        `/materials?${urlParams.toString()}`,
        {
          body: JSON.stringify(params),
        },
      )

      if (data?.status === 'success') {
        setJoints((prev) =>
          page === 1 ? data.results : [...prev, ...data.results],
        )
        setMoreAvailable(data.more)
      } else {
      }
    } catch (error) {
    } finally {
      setLoading(false)
    }
  }

  const handleLoadMore = () => {
    const nextPage = page + 1
    fetchJoints({
      page: nextPage,
      limit,
      category: 'joint',
      search: debouncedSearchTerm,
    })
    setPage(nextPage)
  }

  const selectForDelete = (materialId) => {
    if (!userPermissions.includes('joint:delete')) return

    if (deleteJoints.includes(materialId)) {
      setDeleteJoint((pre) => pre.filter((val) => val !== materialId))
    } else {
      setDeleteJoint((pre) => [...pre, materialId])
    }
  }

  const selectAllForeDelete = () => {
    if (!userPermissions.includes('joint:delete')) return

    if (deleteJoints.length === joints.length) {
      setDeleteJoint([])
    } else {
      setDeleteJoint(joints.map((joint) => joint._id))
    }
  }

  const deleteJointsHandler = async () => {
    if (!userPermissions.includes('joint:delete')) return
    setIsDeleting(true)

    if (!deleteJoints.length) {
      alert('Select materials to delete.')
      setIsDeleting(false)
      return
    }

    try {
      const response = await axiosInterceptorInstance.delete(`/joints/delete`, {
        data: { deleteMaterials: deleteJoints },
      })
      if (response.data.success) {
        setJoints((pre) => {
          return pre.filter((joint) => {
            return !deleteJoints.some((id) => id === joint._id)
          })
        })
      }
      CustomToast({
        message: 'Joint Delete successfully.',
        type: 'success',
      })
    } catch (error) {
      CustomToast({
        message: 'Failed to  delete joint!',
      })
    } finally {
      setIsDeleting(false)
      setDeleteJoint([])
    }
  }

  return (
    <div>
      <div className='flex gap-4 justify-between items-center flex-wrap'>
        {deleteJoints.length && userPermissions.includes('joint:delete') ? (
          <Button
            label='Delete'
            disabled={isDeleting}
            isLoading={isDeleting}
            onClick={deleteJointsHandler}
            className=' flex  gap-1.5 items-center font-medium rounded-lg  disabled:pointer-events-none  disabled:opacity-70 border-2 !border-red-700 hover:!bg-red-700 !text-red-700 hover:!text-white '
          >
            <MdOutlineDelete className='text-2xl' />
          </Button>
        ) : null}

        {deleteJoints.length && userPermissions.includes('joint:delete') ? (
          <label className='flex gap-2 items-center font-semibold'>
            <input
              className='w-5 h-5'
              type='checkbox'
              checked={deleteJoints.length == joints.length}
              onChange={selectAllForeDelete}
            />
            Select All
          </label>
        ) : null}

        <input
          placeholder='Search'
          className='w-full sm:w-[300px]  ml-auto border-[2px] border-prime rounded-lg p-3 focus:outline-none focus:border-orange invalid:border-red-700'
          type='text'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {loading ? <p className='text-center my-20'>Loading...</p> : ''}

      {!loading && joints.length === 0 ? (
        <p className='text-center my-10'>No joints available.</p>
      ) : (
        ''
      )}
      <div className='grid  grid-cols-2 sm:grid-cols-4 md:grid-cols-5 gap-4 my-8'>
        {joints.map((joint) => (
          <div key={joint._id} className='rounded'>
            <div className='rounded-xl relative'>
              <div className='relative group '>
                {userPermissions.includes('joint:update') ? (
                  <Link
                    to={`/joint/update/${joint._id}`}
                    className='absolute bottom-2 right-2 bg-prime p-2 text-xl hidden text-white rounded-full group-hover:inline-block '
                  >
                    <CiEdit />
                  </Link>
                ) : null}
                <img
                  className={`w-full h-full object-cover rounded-xl ${
                    deleteJoints?.includes(joint._id)
                      ? 'border-4 border-prime '
                      : ''
                  }`}
                  src={
                    joint.thumbnail
                      ? `${configData.CDN_BASE_URL}/${
                          joint.thumbnail.startsWith('/')
                            ? joint.thumbnail.slice(1)
                            : 'thumbnails/' + joint.thumbnail
                        }`
                      : `${configData.CDN_BASE_URL}/img/home/add1.png`
                  }
                  onError={(e) => (e.target.src = brokenImg)}
                  alt={joint.name || 'Joint Image'}
                  onClick={() =>
                    userPermissions.includes('joint:delete')
                      ? selectForDelete(joint._id)
                      : null
                  }
                />
                {userPermissions.includes('joint:delete') ? (
                  <input
                    type='checkbox'
                    className={`w-4 h-4 absolute top-4 right-4 ${
                      deleteJoints?.includes(joint._id) ? 'block' : 'hidden'
                    }`}
                    checked={deleteJoints?.includes(joint._id)}
                    onChange={() => selectForDelete(joint._id)}
                  />
                ) : null}
              </div>
            </div>
            <div className='py-3 px-2 flex justify-between items-center'>
              <div>
                {joint.name && (
                  <div className='font-semibold'>{joint.name}</div>
                )}
                {joint.category && (
                  <div className='text-gray-600'>{joint.category}</div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {moreAvailable && joints.length ? (
        <Button
          onClick={handleLoadMore}
          disabled={loading}
          isLoading={loading}
          label='Explore More'
          className='w-fit mx-auto rounded-full !px-8 border-2 border-prime hover:bg-prime hover:text-white flex gap-2 items-center justify-between transition-all divide-purple-300'
        />
      ) : (
        ''
      )}
    </div>
  )
}

export default Joints
